import { useEffect, useMemo, useState, useCallback } from "react";
import { Shuffle, ChevronLeft, ChevronRight, Check, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";

export default function FlashCards({ cards: cardsProp }) {
    const isStandalone = !cardsProp;
    const [allCards, setAllCards] = useState(cardsProp || []);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [statusMap, setStatusMap] = useState({});

    useEffect(() => {
        if (!isStandalone) return;
        api.get("/flashcards").then((r) => setAllCards(r.data));
        api.get("/flashcards/categories").then((r) => setCategories(r.data));
        api.get("/flashcards/status/me").then((r) => {
            const m = {}; r.data.forEach((s) => { m[s.card_id] = s.status; });
            setStatusMap(m);
        });
    }, [isStandalone]);

    const cards = useMemo(() => {
        let list = allCards;
        if (activeCat !== "All") list = list.filter((c) => c.category === activeCat);
        return list;
    }, [allCards, activeCat]);

    return (
        <div className="space-y-8" data-testid="flashcards-page">
            {isStandalone && (
                <>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Memory work</div>
                        <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Flash cards.</h1>
                        <p className="mt-3 text-base text-ink-500 max-w-xl">
                            Drill terminology, scripts, objections, and the call flow until they feel like reflex.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {["All", ...categories].map((c) => (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`flashcard-category-${c}`}
                                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                                    activeCat === c
                                        ? "bg-haevn-700 text-white border-haevn-700"
                                        : "bg-white text-ink-500 border-ink-300 hover:border-haevn-700 hover:text-ink-900"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {cards.length === 0 ? (
                <EmptyState
                    title="No flash cards"
                    description={isStandalone ? "Cards will appear once your administrator adds them." : "No cards in this module."}
                    testId="flashcards-empty"
                />
            ) : (
                <FlashCardDeck cards={cards} statusMap={statusMap} setStatusMap={setStatusMap} />
            )}
        </div>
    );
}

export function FlashCardDeck({ cards, statusMap = {}, setStatusMap = () => {} }) {
    const [i, setI] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [order, setOrder] = useState(cards.map((_, idx) => idx));

    useEffect(() => {
        setOrder(cards.map((_, idx) => idx));
        setI(0); setFlipped(false);
    }, [cards.length]);

    const card = cards[order[i] ?? 0];

    const next = useCallback(() => {
        setFlipped(false);
        setI((v) => (v + 1) % order.length);
    }, [order.length]);
    const prev = useCallback(() => {
        setFlipped(false);
        setI((v) => (v - 1 + order.length) % order.length);
    }, [order.length]);

    const shuffle = () => {
        const o = [...order]; for (let j = o.length - 1; j > 0; j--) {
            const k = Math.floor(Math.random() * (j + 1)); [o[j], o[k]] = [o[k], o[j]];
        }
        setOrder(o); setI(0); setFlipped(false);
    };

    const reset = () => { setOrder(cards.map((_, idx) => idx)); setI(0); setFlipped(false); };

    const mark = async (status) => {
        if (!card) return;
        try {
            await api.post(`/flashcards/${card.id}/mark`, { status });
            setStatusMap((m) => ({ ...m, [card.id]: status }));
        } catch { /* ignore for non-auth contexts */ }
        next();
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === " " || e.key === "Enter") { e.preventDefault(); setFlipped((v) => !v); }
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "k") mark("known");
            if (e.key === "r") mark("review");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line
    }, [next, prev, card]);

    if (!card) return null;

    const status = statusMap[card.id];

    return (
        <div className="max-w-3xl mx-auto" data-testid="flashcard-deck">
            <div className="flex items-center justify-between mb-4 text-xs font-mono uppercase tracking-[0.15em] text-ink-500">
                <span>{i + 1} / {order.length}</span>
                <div className="flex items-center gap-3">
                    {card.category && <span className="text-ink-400">{card.category}</span>}
                    {status === "known" && <span className="text-haevn-700">Known</span>}
                    {status === "review" && <span className="text-clay-500">Review</span>}
                </div>
            </div>

            <div
                className="perspective select-none"
                onClick={() => setFlipped((v) => !v)}
                data-testid="flashcard"
            >
                <div className={`relative w-full aspect-[5/3] preserve-3d transition-transform duration-500 ${flipped ? "rotate-y-180" : ""}`}>
                    <div className="absolute inset-0 backface-hidden bg-white border border-ink-300 p-10 md:p-14 flex flex-col items-center justify-center text-center cursor-pointer hover:border-haevn-700 transition-colors">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-6">Front</div>
                        <div className="font-editorial text-3xl md:text-4xl text-ink-900 leading-tight max-w-2xl">{card.front}</div>
                        <div className="mt-8 text-[11px] font-mono uppercase tracking-[0.15em] text-ink-400">Tap or press space to flip</div>
                    </div>
                    <div className="absolute inset-0 backface-hidden rotate-y-180 bg-haevn-700 text-white p-10 md:p-14 flex flex-col items-center justify-center text-center cursor-pointer">
                        <div className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono mb-6">Back</div>
                        <div className="font-body text-lg md:text-xl leading-relaxed max-w-2xl whitespace-pre-line">{card.back}</div>
                    </div>
                </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-2">
                <button onClick={prev} data-testid="flashcard-prev" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900 transition-colors">
                    <ChevronLeft size={14} /> Prev
                </button>
                <div className="flex gap-2">
                    <button onClick={() => mark("review")} data-testid="flashcard-review-btn" className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] border border-ink-300 hover:border-clay-500 hover:text-clay-500 transition-colors">Review again</button>
                    <button onClick={() => mark("known")} data-testid="flashcard-known-btn" className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] bg-haevn-700 text-white hover:bg-haevn-900 transition-colors">Known</button>
                </div>
                <button onClick={next} data-testid="flashcard-next" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900 transition-colors">
                    Next <ChevronRight size={14} />
                </button>
            </div>

            <div className="mt-8 flex items-center justify-center gap-6 text-[11px] font-mono uppercase tracking-[0.15em] text-ink-400">
                <button onClick={shuffle} data-testid="flashcard-shuffle" className="inline-flex items-center gap-1 hover:text-ink-900"><Shuffle size={12} /> Shuffle</button>
                <button onClick={reset} className="inline-flex items-center gap-1 hover:text-ink-900"><RotateCcw size={12} /> Reset</button>
            </div>
        </div>
    );
}
